import {
	DINING_HALL_SEATS_MAX,
	DINING_HALL_SEATS_MIN,
	type FnbFormMessages,
	TAKEAWAY_PACKS_MAX,
	ACCREDITATION_NAME_MAX,
	CITY_MAX,
	TV_SHORT_NAME_MAX,
	type AccreditationFormMessages,
	makeCreateAccreditationForm,
	makeParticipantContactForm,
	makeEntryPointForm,
	makePatchAccreditationForm,
	makeTravelLegForm,
	TRAVEL_CONNECTIONS_MAX,
	TRAVEL_GROUP_RANGE_DAYS_MAX,
	type TravelFormMessages,
	type TransportFleetFormMessages,
	type TransportFormMessages,
	DRIVE_MINUTES_MAX,
	DRIVE_MINUTES_MIN,
	TRANSPORT_ALLOCATION_ROWS_MAX,
	TRANSPORT_PASSENGERS_MAX,
	TRANSPORT_SUGGESTION_RANGE_DAYS_MAX,
	makeEntryWindowForm,
	makePatchEntryWindowForm,
	makePatchRegistrationWindowForm,
	makeRegistrationWindowForm,
	EVENT_SPORT_CLASSES_MAX,
	GROUP_DESCRIPTION_MAX,
	GROUP_NAME_MAX,
	GROUP_SCOPES_MAX,
	GROUP_SPORTS_MAX,
	MAX_ENTRIES_PER_NOC_LIMIT,
	MAX_SHORTLIST_PER_NOC_LIMIT,
	MEDAL_COUNT_MAX,
	QUALIFYING_NOTE_MAX,
	REFERENCE_NAME_MAX,
	SORT_ORDER_MAX,
	TEAM_SIZE_LIMIT,
	type AccessCodeFormMessages,
	type AffiliatedOrganizationFormMessages,
	type CardHandoverLocationFormMessages,
	type OrganizationTypeFormMessages,
	type ChangePasswordFormMessages,
	type CountryFormMessages,
	type DisciplineFormMessages,
	type EventFormMessages,
	type FunctionFormMessages,
	type ReferenceFormMessages,
	type ResponsibleOrganizationFormMessages,
	type SportClassFormMessages,
	type SportFormMessages,
	type SubAgencyFormMessages,
	type UserFormMessages,
	type UserGroupFormMessages,
	type VenueFormMessages,
	makeAccessCodeForm,
	makeAffiliatedOrganizationForm,
	makeCardHandoverLocationForm,
	makeOrganizationTypeForm,
	makeChangePasswordPageForm,
	makeCreateCountryForm,
	makeCreateUserForm,
	makeCreateUserGroupForm,
	makeDisciplineForm,
	makeEventForm,
	makeLoginForm,
	makeMainFunctionForm,
	makeMfaVerifyForm,
	makeResetPasswordForm,
	makeResponsibleOrganizationForm,
	makeSportClassForm,
	makeSportForm,
	makeSubAgencyForm,
	makeSubFunctionForm,
	makeUpdateCountryForm,
	makeSetGroupPermissionsRequest,
	makeSetGroupScopesRequest,
	makeSetGroupSportsRequest,
	makeUpdateUserForm,
	makeUpdateUserGroupForm,
	makeVenueForm,
} from "@seagames/contracts";
import type { ConnectionMessages } from "../../components/travel-leg-form/model";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * The zod form schemas from `@seagames/contracts`, rebuilt with the viewer's
 * language. The contracts ship English defaults (the wire copy the API also
 * uses); these hooks feed the same factories the `validation` namespace instead,
 * and re-run when the language changes so resolver messages follow along.
 */

type ValidationT = ReturnType<typeof useTranslation<"validation">>["t"];

const userMessages = (t: ValidationT): UserFormMessages => ({
	usernameMin: t("users.usernameMin"),
	usernameFormat: t("users.usernameFormat"),
	usernameNumeric: t("users.usernameNumeric"),
	emailInvalid: t("users.emailInvalid"),
	phoneMin: t("users.phoneMin"),
	passwordMin: t("users.passwordMin"),
	nameRequired: t("users.nameRequired"),
	rolesRequired: t("users.rolesRequired"),
	confirmPasswordRequired: t("users.confirmPasswordRequired"),
	newPasswordRequired: t("users.newPasswordRequired"),
});

const countryMessages = (t: ValidationT): CountryFormMessages => ({
	codeExactThree: t("countries.codeExactThree"),
	callingCodeFormat: t("countries.callingCodeFormat"),
	urlInvalid: t("countries.urlInvalid"),
	nameRequired: t("countries.nameRequired"),
});

export function useCreateUserFormSchema() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeCreateUserForm(userMessages(t)), [i18n.language]);
}

export function useUpdateUserFormSchema() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeUpdateUserForm(userMessages(t)), [i18n.language]);
}

export function useResetPasswordFormSchema() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeResetPasswordForm(userMessages(t)), [i18n.language]);
}

/**
 * The user-group forms and the three replace-all bodies (scopes, permissions,
 * sports), in one hook — the group pages validate each save against the same
 * schema the API uses, with the viewer's copy. Limits come from the contracts.
 */
export function useUserGroupFormSchemas() {
	const { t, i18n } = useTranslation("validation");
	return useMemo(() => {
		const m: UserGroupFormMessages = {
			nameRequired: t("userGroups.nameRequired"),
			nameMax: t("userGroups.nameMax", { max: GROUP_NAME_MAX }),
			descriptionMax: t("userGroups.descriptionMax", { max: GROUP_DESCRIPTION_MAX }),
			roleRequired: t("userGroups.roleRequired"),
			scopeEmpty: t("userGroups.scopeEmpty"),
			scopesMax: t("userGroups.scopesMax", { max: GROUP_SCOPES_MAX }),
			scopeDuplicate: t("userGroups.scopeDuplicate"),
			permissionDuplicate: t("userGroups.permissionDuplicate"),
			sportsMax: t("userGroups.sportsMax", { max: GROUP_SPORTS_MAX }),
			sportDuplicate: t("userGroups.sportDuplicate"),
		};
		return {
			create: makeCreateUserGroupForm(m),
			update: makeUpdateUserGroupForm(m),
			scopes: makeSetGroupScopesRequest(m),
			permissions: makeSetGroupPermissionsRequest(m),
			sports: makeSetGroupSportsRequest(m),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	}, [i18n.language]);
}

/** Both country schemas in one hook — the modal hosts create and edit forms. */
export function useCountryFormSchema() {
	const { t, i18n } = useTranslation("validation");
	return useMemo(
		() => ({
			create: makeCreateCountryForm(countryMessages(t)),
			update: makeUpdateCountryForm(countryMessages(t)),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
		[i18n.language],
	);
}

/** Reference-data forms: one schema serves both create and edit. */

export function useResponsibleOrganizationFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): ResponsibleOrganizationFormMessages => ({ nameRequired: t("responsibleOrganizations.nameRequired") });
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeResponsibleOrganizationForm(messages()), [i18n.language]);
}

export function useSubAgencyFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): SubAgencyFormMessages => ({ nameRequired: t("subAgencies.nameRequired") });
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeSubAgencyForm(messages()), [i18n.language]);
}

export function useAffiliatedOrganizationFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): AffiliatedOrganizationFormMessages => ({ nameRequired: t("affiliatedOrganizations.nameRequired") });
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeAffiliatedOrganizationForm(messages()), [i18n.language]);
}

const functionMessages = (t: ValidationT): FunctionFormMessages => ({
	nameRequired: t("functions.nameRequired"),
	codeRequired: t("functions.codeRequired"),
	codeFormat: t("functions.codeFormat"),
	cardColorFormat: t("functions.cardColorFormat"),
	sortOrderInvalid: t("functions.sortOrderInvalid"),
});

/** Both function schemas in one hook — the pages host main and sub forms. */
export function useFunctionFormSchemas() {
	const { t, i18n } = useTranslation("validation");
	return useMemo(
		() => ({
			main: makeMainFunctionForm(functionMessages(t)),
			sub: makeSubFunctionForm(functionMessages(t)),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
		[i18n.language],
	);
}

/**
 * Competition setup forms (sport, discipline, event, para class, venue). One
 * `validation.competition` group serves all five; the limits in the messages
 * are the contracts' constants, so the copy cannot quote a stale number.
 */
const referenceMessages = (t: ValidationT): ReferenceFormMessages => ({
	nameRequired: t("competition.nameRequired"),
	nameTooLong: t("competition.nameTooLong", { max: REFERENCE_NAME_MAX }),
	codeRequired: t("competition.codeRequired"),
	codeFormat: t("competition.codeFormat"),
	sortOrderInvalid: t("competition.sortOrderInvalid", { max: SORT_ORDER_MAX }),
	translationTooLong: t("competition.translationTooLong", { max: REFERENCE_NAME_MAX }),
});

export function useSportFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): SportFormMessages => ({ ...referenceMessages(t), iconUrlInvalid: t("competition.iconUrlInvalid") });
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeSportForm(messages()), [i18n.language]);
}

export function useDisciplineFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): DisciplineFormMessages => referenceMessages(t);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeDisciplineForm(messages()), [i18n.language]);
}

export function useEventFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): EventFormMessages => ({
		...referenceMessages(t),
		disciplineRequired: t("competition.disciplineRequired"),
		genderRequired: t("competition.genderRequired"),
		competitorTypeRequired: t("competition.competitorTypeRequired"),
		resultFamilyRequired: t("competition.resultFamilyRequired"),
		resultTypeRequired: t("competition.resultTypeRequired"),
		teamSizeInvalid: t("competition.teamSizeInvalid", { max: TEAM_SIZE_LIMIT }),
		teamSizeTeamOnly: t("competition.teamSizeTeamOnly"),
		teamSizeMinOverMax: t("competition.teamSizeMinOverMax"),
		medalCountInvalid: t("competition.medalCountInvalid", { max: MEDAL_COUNT_MAX }),
		maxEntriesInvalid: t("competition.maxEntriesInvalid", { max: MAX_ENTRIES_PER_NOC_LIMIT }),
		maxShortlistInvalid: t("competition.maxShortlistInvalid", { max: MAX_SHORTLIST_PER_NOC_LIMIT }),
		maxShortlistBelowEntries: t("competition.maxShortlistBelowEntries"),
		qualifyingMarkInvalid: t("competition.qualifyingMarkInvalid"),
		qualifyingMarkNoMeasure: t("competition.qualifyingMarkNoMeasure"),
		qualifyingNoteTooLong: t("competition.qualifyingNoteTooLong", { max: QUALIFYING_NOTE_MAX }),
		ceremonyInvalid: t("competition.ceremonyInvalid"),
		sportClassesTooMany: t("competition.sportClassesTooMany", { max: EVENT_SPORT_CLASSES_MAX }),
		sportClassesDuplicate: t("competition.sportClassesDuplicate"),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeEventForm(messages()), [i18n.language]);
}

export function useSportClassFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): SportClassFormMessages => ({
		codeRequired: t("competition.codeRequired"),
		codeFormat: t("competition.codeFormat"),
		nameTooLong: t("competition.nameTooLong", { max: REFERENCE_NAME_MAX }),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeSportClassForm(messages()), [i18n.language]);
}

export function useVenueFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const { t: tf } = useTranslation("transportFleet");
	const messages = (): VenueFormMessages => ({
		nameRequired: t("competition.nameRequired"),
		nameTooLong: t("competition.nameTooLong", { max: REFERENCE_NAME_MAX }),
		codeRequired: t("competition.codeRequired"),
		codeFormat: t("competition.codeFormat"),
		cityTooLong: t("competition.cityTooLong", { max: CITY_MAX }),
		parentInvalid: t("competition.parentInvalid"),
		// M10: the venue's transport cluster (copy in the fleet namespace).
		clusterInvalid: tf("clusterField.invalid"),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeVenueForm(messages()), [i18n.language]);
}

/** Sign-in, 2FA and change-password: the same wire schemas the API validates, with translated messages. */

export function useLoginFormSchema() {
	const { t, i18n } = useTranslation("validation");
	return useMemo(
		() => makeLoginForm({ identifierRequired: t("auth.identifierRequired"), passwordRequired: t("auth.passwordRequired") }),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
		[i18n.language],
	);
}

export function useMfaVerifyFormSchema() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeMfaVerifyForm({ codeRequired: t("auth.codeRequired") }), [i18n.language]);
}

/** The change-password page: no Current box on the forced first change (`/auth/me.must_change_password`). */
export function useChangePasswordFormSchema(forced: boolean) {
	const { t, i18n } = useTranslation("validation");
	const messages = (): ChangePasswordFormMessages => ({
		currentPasswordRequired: t("auth.currentPasswordRequired"),
		newPasswordMin: t("auth.newPasswordMin"),
		newPasswordSame: t("auth.newPasswordSame"),
		confirmPasswordMismatch: t("auth.confirmPasswordMismatch"),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeChangePasswordPageForm(forced, messages()), [i18n.language, forced]);
}

/** Game Settings — organisation types, access codes, card handover locations. */

export function useOrganizationTypeFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): OrganizationTypeFormMessages => ({
		codeRequired: t("organizationTypes.codeRequired"),
		codeFormat: t("organizationTypes.codeFormat"),
		nameRequired: t("organizationTypes.nameRequired"),
		subAgencyLabelRequired: t("organizationTypes.subAgencyLabelRequired"),
		sortOrderInvalid: t("organizationTypes.sortOrderInvalid"),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeOrganizationTypeForm(messages()), [i18n.language]);
}

export function useAccessCodeFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): AccessCodeFormMessages => ({
		codeRequired: t("accessCodes.codeRequired"),
		codeFormat: t("accessCodes.codeFormat"),
		nameRequired: t("accessCodes.nameRequired"),
		sortOrderInvalid: t("accessCodes.sortOrderInvalid"),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeAccessCodeForm(messages()), [i18n.language]);
}

export function useHandoverLocationFormSchema() {
	const { t, i18n } = useTranslation("validation");
	const messages = (): CardHandoverLocationFormMessages => ({
		nameRequired: t("handoverLocations.nameRequired"),
		sortOrderInvalid: t("handoverLocations.sortOrderInvalid"),
	});
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeCardHandoverLocationForm(messages()), [i18n.language]);
}

/** Accreditation — the Register flow and Settings → Registration windows (M3 S5). */
function accreditationMessages(t: ValidationT): AccreditationFormMessages {
	return {
		required: t("accreditation.required"),
		textTooLong: t("accreditation.textTooLong"),
		accreditationNameMax: t("accreditation.accreditationNameMax", { max: ACCREDITATION_NAME_MAX }),
		tvShortNameMax: t("accreditation.tvShortNameMax", { max: TV_SHORT_NAME_MAX }),
		emailInvalid: t("accreditation.emailInvalid"),
		dateInvalid: t("accreditation.dateInvalid"),
		dateTimeInvalid: t("accreditation.dateTimeInvalid"),
		countryInvalid: t("accreditation.countryInvalid"),
		numberInvalid: t("accreditation.numberInvalid"),
		remarksRequired: t("accreditation.remarksRequired"),
		personOrPeopleId: t("accreditation.personOrPeopleId"),
		closesAfterOpens: t("accreditation.closesAfterOpens"),
		nothingToChange: t("accreditation.nothingToChange"),
		idsOrFilter: t("accreditation.idsOrFilter"),
	};
}

/** The Register flow's bodies (`POST` / `PATCH /accreditations`) with localized messages, plus the messages themselves for the form's own checks. */
export function useAccreditationFormSchemas() {
	const { t, i18n } = useTranslation("validation");
	const travel = useTravelFormMessages();
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => {
		const messages = accreditationMessages(t);
		return { messages, travel, create: makeCreateAccreditationForm(messages, travel), patch: makePatchAccreditationForm(messages, travel) };
	}, [i18n.language, travel]);
}

/** A participant's own contact details (`PATCH /me/profile`): the accreditation messages, without the version stamp (the page adds it). */
export function useParticipantContactFormSchema() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => makeParticipantContactForm(accreditationMessages(t)).omit({ version: true }), [i18n.language]);
}

/**
 * Travel (M9) — the leg form, the A&D bulk / range forms and the entry point
 * form share the contracts' `TravelFormMessages`. Their copy lives with the
 * leg form, in `travel.json` → `legForm.validation.*` (not `validation.json`):
 * one namespace for everything the travel screens say.
 */
export function useTravelFormMessages(): TravelFormMessages & ConnectionMessages {
	const { t, i18n } = useTranslation("travel");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(
		() => ({
			required: t("legForm.validation.required"),
			textTooLong: t("legForm.validation.textTooLong"),
			dateInvalid: t("legForm.validation.dateInvalid"),
			dateTimeInvalid: t("legForm.validation.dateTimeInvalid"),
			flightInvalid: t("legForm.validation.flightInvalid"),
			flightOnlyForAir: t("legForm.validation.flightOnlyForAir"),
			delayWithFlight: t("legForm.validation.delayWithFlight"),
			departureBeforeArrival: t("legForm.validation.departureBeforeArrival"),
			rangeInvalid: t("legForm.validation.rangeInvalid"),
			rangeTooLong: t("legForm.validation.rangeTooLong", { count: TRAVEL_GROUP_RANGE_DAYS_MAX }),
			idsDuplicate: t("legForm.validation.idsDuplicate"),
			nothingToChange: t("legForm.validation.nothingToChange"),
			landedBeforeDeparted: t("legForm.validation.landedBeforeDeparted"),
			codeInvalid: t("legForm.validation.codeInvalid"),
			nameRequired: t("legForm.validation.nameRequired"),
			iataInvalid: t("legForm.validation.iataInvalid"),
			iataOnlyForAir: t("legForm.validation.iataOnlyForAir"),
			minutesInvalid: t("legForm.validation.minutesInvalid"),
			sortOrderInvalid: t("legForm.validation.sortOrderInvalid"),
			flightNumberRequired: t("legForm.validation.flightNumberRequired"),
			connectionsNeedFlight: t("legForm.validation.connectionsNeedFlight"),
			connectionsTooMany: t("legForm.validation.connectionsTooMany", { count: TRAVEL_CONNECTIONS_MAX }),
			connectionsDuplicate: t("legForm.validation.connectionsDuplicate"),
			transportTypeMismatch: t("legForm.validation.transportTypeMismatch"),
			transportTypeOtherRequired: t("legForm.validation.transportTypeOtherRequired"),
			// The leg form's own chain checks (`validateLegDraft`), beside the contract's messages.
			connectionNeedsLookup: t("legForm.validation.connectionNeedsLookup"),
			connectionRoute: t("legForm.validation.connectionRoute"),
			connectionTime: t("legForm.validation.connectionTime"),
		}),
		[i18n.language],
	);
}

/** `PUT /accreditations/:id/travel/:direction` (the record page's Edit sheet), localized. */
export function useTravelLegFormSchema() {
	const m = useTravelFormMessages();
	return useMemo(() => makeTravelLegForm(m), [m]);
}

/** Game Settings → Entry points, localized (`makeEntryPointForm`; M10 adds the transport cluster's message). */
export function useEntryPointFormSchema() {
	const m = useTravelFormMessages();
	const { t: tf } = useTranslation("transportFleet");
	const clusterInvalid = tf("clusterField.invalid");
	return useMemo(() => makeEntryPointForm({ ...m, clusterInvalid }), [m, clusterInvalid]);
}

export function useRegistrationWindowFormSchemas() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => {
		const messages = accreditationMessages(t);
		return { create: makeRegistrationWindowForm(messages), patch: makePatchRegistrationWindowForm(messages) };
	}, [i18n.language]);
}

/** M7b entry windows: the registration windows' three messages (same `validation` keys). */
export function useEntryWindowFormSchemas() {
	const { t, i18n } = useTranslation("validation");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(() => {
		const m = accreditationMessages(t);
		const messages = { required: m.required, dateTimeInvalid: m.dateTimeInvalid, closesAfterOpens: m.closesAfterOpens };
		return { create: makeEntryWindowForm(messages), patch: makePatchEntryWindowForm(messages) };
	}, [i18n.language]);
}

// ── M10 Transportation ──────────────────────────────────────────────────────
// Both transport slices' contract messages, localized. The copy lives in
// `transportFleet.json` (`formMessages.*` for the fleet schemas,
// `tripMessages.*` for the request-side ones), so one namespace answers for
// every transport form: `makeCreateVehicleRequest(useTransportFleetFormMessages())`,
// `makeCreateTransportRequest(useTransportFormMessages())`.

/** `TransportFleetFormMessages` — vehicles, drivers, companies, clusters, drive times, places, drop-offs, settings. */
export function useTransportFleetFormMessages(): TransportFleetFormMessages {
	const { t, i18n } = useTranslation("transportFleet");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(
		() => ({
			required: t("formMessages.required"),
			textTooLong: t("formMessages.textTooLong"),
			dateInvalid: t("formMessages.dateInvalid"),
			nameRequired: t("formMessages.nameRequired"),
			nothingToChange: t("formMessages.nothingToChange"),
			idsDuplicate: t("formMessages.idsDuplicate"),
			codeInvalid: t("formMessages.codeInvalid"),
			plateInvalid: t("formMessages.plateInvalid"),
			seatsInvalid: t("formMessages.seatsInvalid"),
			phoneInvalid: t("formMessages.phoneInvalid"),
			emailInvalid: t("formMessages.emailInvalid"),
			vehicleTypesRequired: t("formMessages.vehicleTypesRequired"),
			timeInvalid: t("formMessages.timeInvalid"),
			shiftEmpty: t("formMessages.shiftEmpty"),
			restDayInvalid: t("formMessages.restDayInvalid"),
			minutesInvalid: t("formMessages.minutesInvalid", { min: DRIVE_MINUTES_MIN, max: DRIVE_MINUTES_MAX }),
			numberInvalid: t("formMessages.numberInvalid"),
			sortOrderInvalid: t("formMessages.sortOrderInvalid"),
			rowsMax: t("formMessages.rowsMax"),
			pairInvalid: t("formMessages.pairInvalid"),
		}),
		[i18n.language],
	);
}

/** `TransportFormMessages` — requests, transfers, passengers, cancel / reject, allocations (S4's dispatch forms and S5's Request a trip). */
export function useTransportFormMessages(): TransportFormMessages {
	const { t, i18n } = useTranslation("transportFleet");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(
		() => ({
			required: t("tripMessages.required"),
			textTooLong: t("tripMessages.textTooLong"),
			dateInvalid: t("tripMessages.dateInvalid"),
			dateTimeInvalid: t("tripMessages.dateTimeInvalid"),
			rangeInvalid: t("tripMessages.rangeInvalid"),
			rangeTooLong: t("tripMessages.rangeTooLong", { count: TRANSPORT_SUGGESTION_RANGE_DAYS_MAX }),
			idsDuplicate: t("tripMessages.idsDuplicate"),
			nothingToChange: t("tripMessages.nothingToChange"),
			samePlace: t("tripMessages.samePlace"),
			entryPointEnd: t("tripMessages.entryPointEnd"),
			returnInvalid: t("tripMessages.returnInvalid"),
			timeConflict: t("tripMessages.timeConflict"),
			passengersRequired: t("tripMessages.passengersRequired"),
			passengersMax: t("tripMessages.passengersMax", { max: TRANSPORT_PASSENGERS_MAX }),
			countInvalid: t("tripMessages.countInvalid"),
			wheelchairTooMany: t("tripMessages.wheelchairTooMany"),
			minutesInvalid: t("tripMessages.minutesInvalid", { min: DRIVE_MINUTES_MIN, max: DRIVE_MINUTES_MAX }),
			rowsMax: t("tripMessages.rowsMax", { max: TRANSPORT_ALLOCATION_ROWS_MAX }),
			reasonRequired: t("tripMessages.reasonRequired"),
		}),
		[i18n.language],
	);
}

// ── M12 Food & Beverages ────────────────────────────────────────────────────

/** `FnbFormMessages` — dining halls, takeaway orders, status moves, scans: `makeCreateDiningHallBody(useFnbFormMessages())`. */
export function useFnbFormMessages(): FnbFormMessages {
	const { t, i18n } = useTranslation("fnb");
	// eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable per language
	return useMemo(
		() => ({
			required: t("formMessages.required"),
			textTooLong: t("formMessages.textTooLong"),
			invalidTime: t("formMessages.invalidTime"),
			sessionsAllMeals: t("formMessages.sessionsAllMeals"),
			sessionEndsAfterStart: t("formMessages.sessionEndsAfterStart"),
			duplicateDiet: t("formMessages.duplicateDiet"),
			duplicateBuilding: t("formMessages.duplicateBuilding"),
			seatsRange: t("formMessages.seatsRange", { min: DINING_HALL_SEATS_MIN, max: DINING_HALL_SEATS_MAX }),
			packsRange: t("formMessages.packsRange", { max: TAKEAWAY_PACKS_MAX }),
			dietPacksOver: t("formMessages.dietPacksOver"),
			noteRequired: t("formMessages.noteRequired"),
			scanTarget: t("formMessages.scanTarget"),
			nothingToChange: t("formMessages.nothingToChange"),
			duplicateId: t("formMessages.duplicateId"),
			duplicateChoice: t("formMessages.duplicateChoice"),
		}),
		[i18n.language],
	);
}
